import React, { useEffect, useRef, useState } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { useTenancy } from '@/contexts/TenancyContext';
import { useMSGReadOnly } from '../../contexts/ReadOnlyContext';
import { db } from '@/js/external-js/config/main_config';

import SupportingDocsDropdown from '../../components/SupportingDocsDropdown';

import { fetchRelatedDocuments, populateCustomContent, fetchPageContentMDX } from '@/app/pages/msg/utils/common';
import RelatedClausesAccordianItem from '@/app/pages/msg/components/RelatedClauses';
import { MarkdownRenderer } from '@/app/pages/msg/components/MarkdownRenderer';
import { useMarkdownComponents } from '@/app/pages/msg/components/MDXFields';
import { getAllModules } from '@/app/pages/msg/utils/common';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Pencil, Plus, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import scopeService, { ScopeData } from '@/services/scopeService';
import AdditionalContent from '../../components/AdditionalContent';
// import SupportingDocs from '../../components/SupportingDocs';
import FootNote from '../../components/FootNote';
import { useMSGVersion } from '../../hooks/useMSGVersion';
import { Checkbox } from '@/components/ui/checkbox';
import { getSOAExclusions } from '@/services/soaService';
import { Control } from '@/types/soa';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function ScopeSection() {
  const { activeTenancy, currentUser, isModuleEnabled } = useTenancy();
  const { isReadOnly } = useMSGReadOnly();
  const { incrementVersion } = useMSGVersion();

  const clauseStandards = useRef<Set<string>>(new Set());

  const [pageClauses, setPageClauses] = useState<Set<string>>(new Set());

  // Function to update pageClauses
  const updatePageClauses = (newClauses: Set<string>) => {
    setPageClauses(new Set(newClauses));
  };

  // State for scope items
  const [scopeData, setScopeData] = useState<ScopeData>({
    heading: 'MSG Scope',
    text: {}
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  // Form state
  const [scopeKey, setScopeKey] = useState('');
  const [scopeValue, setScopeValue] = useState('');

  const [pageTemplateMDX, setPageTemplateMDX] = React.useState<string>('');
  const [modulesList, setModulesList] = useState([]);
  const [supportingDocs, setSupportingDocs] = React.useState([]);
  const [sortedFrameworks, setSortedFrameworks] = React.useState([]);
  const [dynamicStandardNames, setDynamicStandardNames] = useState<Map<string, string>>(new Map());
  const [dynamicContent, setDynamicContent] = React.useState<string>("");

  // SOA Exclusions state
  const [soaExclusions, setSoaExclusions] = useState<Control[]>([]);
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([]);
  const [loadingSOAExclusions, setLoadingSOAExclusions] = useState(false);
  const [frameworksPopoverOpen, setFrameworksPopoverOpen] = useState(false);

  const pageName = 'context';
  const contentPath = ['scope_MDX'];
  const pageRoute = '/MSG/context/scope';

  const defaults = {
    text: {
      informationAssets: 'Assets defined in the Acme Inc asset register as operating within the MSG Scope...',
      location: 'Acme Inc leverage a fully remote workforce. No Physical premesis are in scope for the MSG.',
      overview: 'The delivery and operation of Acme Inc platform inclusive of personnel, IT infrastructure and management systems in accordance with Acme Inc statement of applicability.',
      personnel: 'All employees and contractors of Acme Inc who have direct access to Acme Inc platform.',
      physicalAssets: 'All Acme Inc assets used in the within the MSG.',
      scopeExclusions: 'Scope exclusions will be documented here.',
      software: 'All software used within the scope of the Acme Inc MSG.'
    }
  }
  // Load scope data
  const loadScopeData = async () => {
    if (!activeTenancy.id) return;

    try {
      setIsLoading(true);
      const scopeData = await scopeService.getAllScopeItems(activeTenancy.id);

      // If we have data, use it, otherwise initialize with defaults
      if (Object.keys(scopeData.text || {}).length > 0) {
        // check if each of the default keys exists or empty strings in the scopeData.text object
        // Create a merged text object that uses defaults when scopeData values are missing or empty
        const mergedText = Object.keys(defaults.text).reduce((result, key) => {
          // Use scopeData value if it exists and isn't an empty string, otherwise use default
          result[key] = (
            scopeData.text &&
            key in scopeData.text &&
            scopeData.text[key] &&
            scopeData.text[key].trim() !== ''
          ) ? scopeData.text[key] : defaults.text[key];
          return result;
        }, {});

        // Set the scope data with merged text
        setScopeData({
          heading: scopeData.heading || 'MSG Scope',
          text: mergedText
        });
      } else {
        // Initialize with default structure if no data exists
        setScopeData({
          heading: 'MSG Scope',
          text: defaults.text
        });
      }
    } catch (error) {
      console.error('Error loading scope data:', error);
      toast.error('Failed to load scope data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load SOA exclusions
  const loadSOAExclusions = async () => {
    if (!activeTenancy.id) return;

    try {
      setLoadingSOAExclusions(true);
      const exclusions = await getSOAExclusions(activeTenancy.id, isModuleEnabled);
      setSoaExclusions(exclusions);

      // Load saved framework selection
      const savedFrameworks = await loadSOAExclusionsFrameworks();
      const uniqueFrameworks = Array.from(new Set(exclusions.map(control => control.framework)));

      let frameworksToSelect: string[];

      if (savedFrameworks.length > 0) {
        // Filter saved frameworks to only include ones that currently exist
        const validSavedFrameworks = savedFrameworks.filter(f => uniqueFrameworks.includes(f));
        if (validSavedFrameworks.length > 0) {
          // Use the valid saved frameworks
          frameworksToSelect = validSavedFrameworks;
        } else {
          // No valid saved frameworks, default to all current frameworks but don't save this
          frameworksToSelect = uniqueFrameworks;
        }
      } else {
        // No saved frameworks, default to all current frameworks but don't save this
        frameworksToSelect = uniqueFrameworks;
      }

      setSelectedFrameworks(frameworksToSelect);
    } catch (error) {
      console.error('Error loading SOA exclusions:', error);
      toast.error('Failed to load SOA exclusions. Please try again.');
    } finally {
      setLoadingSOAExclusions(false);
    }
  };

  // Handle save scope data
  const handleSaveScope = async (key: string, value: string) => {
    if (!key || !value) return;
    const oldValue = scopeData.text[key];

    try {
      const updatedBy = currentUser?.email || 'system';
      const updatedText = {
        ...scopeData.text,
        [key]: value
      };

      const updatedData: ScopeData = {
        ...scopeData,
        text: updatedText,
        updatedBy,
        updatedAt: new Date().toISOString()
      };

      incrementVersion(
        'table_row_update',
        'Updated scope item',
        {
          oldData: scopeData,
          newData: updatedData
        }
      );

      // Update or create the scope data
      await scopeService.createScopeItem(activeTenancy.id, updatedData);

      setScopeData(updatedData);
      setEditingKey(null);
      setScopeKey('');
      setScopeValue('');
      toast.success('Scope updated successfully');
    } catch (error) {
      console.error('Error saving scope:', error);
      toast.error('Failed to update scope. Please try again.');
    }
  };

  // Handle delete scope item
  const handleDeleteItem = async (key: string) => {
    if (!confirm(`Are you sure you want to delete the '${key}' scope item?`)) return;

    try {
      const updatedText = { ...scopeData.text };
      delete updatedText[key];

      const updatedData: ScopeData = {
        ...scopeData,
        text: updatedText,
        updatedBy: currentUser?.email || 'system',
        updatedAt: new Date().toISOString()
      };

      await scopeService.createScopeItem(activeTenancy.id, updatedData);
      setScopeData(updatedData);
      toast.success('Scope item deleted successfully');
    } catch (error) {
      console.error('Error deleting scope item:', error);
      toast.error('Failed to delete scope item. Please try again.');
    }
  };

  // Removing the ability to add new scope items since scope areas should be predefined

  useEffect(() => {
    if (activeTenancy.id === "" || activeTenancy.id === "Loading...") return;

    loadScopeData();

    const loadModules = async () => {
      const allModules = await getAllModules();
      const storedVisibilities = JSON.parse(localStorage.getItem('modulesVisibility') || '{}');

      allModules.forEach(module => {
        module.visible = storedVisibilities[module.id] ?? false;
      });

      setModulesList(allModules);
    }

    const getPageContentMDX = fetchPageContentMDX(db, pageName, contentPath, activeTenancy, setPageTemplateMDX);
    // const getRelatedDocuments = fetchRelatedDocuments(db, activeTenancy.id, pageRoute, setSupportingDocs);
    const getCustomContent = populateCustomContent(db, activeTenancy.id, pageRoute, setDynamicContent);

    loadModules();
    loadSOAExclusions();

    return () => {
      getPageContentMDX();
      getCustomContent();
      // getRelatedDocuments();
    };
  }, [activeTenancy]);

  // Helper function to get unique frameworks from exclusions
  const getUniqueFrameworks = (exclusions: Control[]): string[] => {
    return Array.from(new Set(exclusions.map(control => control.framework)));
  };

  // Helper function to filter exclusions by selected frameworks
  const getFilteredExclusions = (): Control[] => {
    if (selectedFrameworks.length === 0) return soaExclusions;
    return soaExclusions.filter(control => selectedFrameworks.includes(control.framework));
  };

  // Handle framework selection
  const handleFrameworkSelect = (framework: string) => {
    const newSelection = selectedFrameworks.includes(framework)
      ? selectedFrameworks.filter(f => f !== framework)
      : [...selectedFrameworks, framework];

    setSelectedFrameworks(newSelection);
    saveSOAExclusionsFrameworks(newSelection); // This saves the correct new value
  };
  // Handle select all frameworks
  const handleSelectAllFrameworks = () => {
    const allFrameworks = getUniqueFrameworks(soaExclusions);
    const allSelected = allFrameworks.every(f => selectedFrameworks.includes(f));

    const newSelection = allSelected ? [] : allFrameworks;
    setSelectedFrameworks(newSelection);
    saveSOAExclusionsFrameworks(newSelection);
  };

  // Save SOA exclusions framework selection to company settings
  const saveSOAExclusionsFrameworks = async (frameworks: string[]) => {
    if (!activeTenancy.id) return;

    try {
      const docPath = `customers/${activeTenancy.id}/companySettings/soaExclusions`;
      const docRef = doc(db, docPath);
      const timestamp = new Date().toISOString();

      await setDoc(docRef, {
        selectedFrameworks: frameworks,
        updatedAt: timestamp
      }, { merge: true });

      console.log('[Scope] Saved SOA exclusions framework selection:', frameworks);
    } catch (error) {
      console.error('[Scope] Error saving SOA exclusions framework selection:', error);
    }
  };

  // Load SOA exclusions framework selection from company settings
  const loadSOAExclusionsFrameworks = async () => {
    if (!activeTenancy.id) return [];

    try {
      const docPath = `customers/${activeTenancy.id}/companySettings/soaExclusions`;
      const docRef = doc(db, docPath);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('[Scope] Loaded SOA exclusions framework selection:', data.selectedFrameworks);
        return data.selectedFrameworks || [];
      }
      return [];
    } catch (error) {
      console.error('[Scope] Error loading SOA exclusions framework selection:', error);
      return [];
    }
  };

  const SCOPE_ITEM_ORDER = [
    'overview',
    'location',
    'personnel',
    'physicalAssets',
    'software',
    'informationAssets',
    'scopeExclusions'
  ];


  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6 relative">
        <div className="absolute right-4 top-4 z-10">
          <div className="flex items-center space-x-2">
            <SupportingDocsDropdown pageRoute={pageRoute} clauseStandards={clauseStandards} pageClauses={pageClauses} />
            <RelatedClausesAccordianItem modulesList={modulesList} clauseStandards={clauseStandards} setModulesList={setModulesList} />
          </div>
        </div>
        <div className="px-6 py-6">
          <MarkdownRenderer source={pageTemplateMDX} components={useMarkdownComponents(modulesList, clauseStandards, { current: pageClauses }, updatePageClauses)} />
        </div>
        <div style={{ maxWidth: '56rem', marginLeft: 'auto', marginRight: 'auto', padding: '0px 1rem 1.5rem ', transition: 'color 0.2s, background-color 0.2s', backgroundColor: 'transparent' }}>
          <AdditionalContent pageRoute={pageRoute} />
          <FootNote />
        </div>
      </div>
      <div className="bg-card rounded-xl border-border shadow-sm px-6 py-6">
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">{scopeData.heading}</h2>
            <div className="h-10"></div>
          </div>
        </div>
        {isLoading ? (
          <div className="p-4 text-center text-muted-foreground">
            Loading scope data...
          </div>
        ) : !scopeData.text || Object.keys(scopeData.text).length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            No scope items found. Add your first scope item to get started.
          </div>
        ) : (
          <Table className="table-auto table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Scope Area</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[150px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(scopeData.text)
                .sort(([keyA], [keyB]) => {
                  const indexA = SCOPE_ITEM_ORDER.indexOf(keyA);
                  const indexB = SCOPE_ITEM_ORDER.indexOf(keyB);
                  // If both keys are in the order array, sort by their position
                  if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                  // If only one key is in the array, it comes first
                  if (indexA !== -1) return -1;
                  if (indexB !== -1) return 1;
                  // If neither key is in the array, sort alphabetically
                  return keyA.localeCompare(keyB);
                })
                .map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell className="font-medium align-top py-4">
                      <span className="capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    </TableCell>
                    <TableCell className="align-top py-4 border-l border-border">
                      {editingKey === key ? (
                        <Textarea
                          value={scopeValue}
                          onChange={(e) => setScopeValue(e.target.value)}
                          className="min-h-[100px] w-full"
                        />
                      ) : (
                        <div className="whitespace-pre-wrap">{value}</div>
                      )}
                    </TableCell>
                    <TableCell className="align-top py-4 border-l border-border">
                      <div className="flex items-center space-x-2">
                        {editingKey === key ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingKey(null);
                                setScopeKey('');
                                setScopeValue('');
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleSaveScope(scopeKey || key, scopeValue || value)}
                            >
                              Save
                            </Button>
                          </>
                        ) : (
                          !isReadOnly && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setScopeKey(key);
                                  setScopeValue(value);
                                  setEditingKey(key);
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                                <span className="sr-only">Edit</span>
                              </Button>
                              {/* <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteItem(key)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                              <span className="sr-only">Delete</span>
                            </Button> */}
                            </>
                          )
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

              {/* Removed add new item functionality */}
            </TableBody>
          </Table>
        )}
      </div>
      <div className="bg-card rounded-xl border-border shadow-sm px-6 py-6">
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">SOA Exclusions</h2>
            <div className="flex items-center space-x-2">
              <Popover open={frameworksPopoverOpen} onOpenChange={setFrameworksPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={frameworksPopoverOpen}
                    className="w-[200px] justify-between"
                    type="button"
                  >
                    {selectedFrameworks.length > 0
                      ? `${selectedFrameworks.length} framework${selectedFrameworks.length > 1 ? 's' : ''} selected`
                      : "Select frameworks..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0">
                  <Command>
                    <CommandInput placeholder="Search frameworks..." />
                    <CommandList className="max-h-[300px] overflow-auto">
                      <CommandEmpty>No frameworks found.</CommandEmpty>
                      <CommandGroup>
                        {/* Select All option */}
                        <CommandItem
                          value="select-all-frameworks"
                          onSelect={handleSelectAllFrameworks}
                          className="font-medium"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              getUniqueFrameworks(soaExclusions).length > 0 && getUniqueFrameworks(soaExclusions).every(f => selectedFrameworks.includes(f)) ? "opacity-100" : "opacity-0"
                            )}
                          />
                          Select All
                        </CommandItem>
                        {getUniqueFrameworks(soaExclusions).map((framework) => (
                          <CommandItem
                            key={framework}
                            value={framework}
                            onSelect={() => {
                              handleFrameworkSelect(framework);
                              // Keep the popover open
                              setFrameworksPopoverOpen(true);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedFrameworks.includes(framework) ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {framework}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
        {loadingSOAExclusions ? (
          <div className="p-4 text-center text-muted-foreground">
            Loading SOA exclusions...
          </div>
        ) : getFilteredExclusions().length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            No SOA exclusions found for selected frameworks.
          </div>
        ) : (
          <Table className="table-auto table">
            <TableHeader>
              <TableRow>
                <TableHead>Control Name</TableHead>
                <TableHead>Control Number</TableHead>
                <TableHead>Control Framework</TableHead>
                <TableHead>Control Justification</TableHead>
                <TableHead>Control Implemented</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getFilteredExclusions().map((control) => (
                <TableRow key={control.id}>
                  <TableCell className="font-medium align-top py-4">
                    {control.name}
                  </TableCell>
                  <TableCell className="align-top py-4">
                    {control.controlID}
                  </TableCell>
                  <TableCell className="align-top py-4">
                    {control.framework}
                  </TableCell>
                  <TableCell className="align-top py-4 w-[300px] whitespace-pre-wrap">
                    {control.justification || 'No justification provided'}
                  </TableCell>
                  <TableCell className="align-top py-4">
                    {control.status}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {/* <div className="bg-card rounded-xl border-border shadow-sm">
        <SupportingDocs pageRoute={pageRoute} clauseStandards={clauseStandards} pageClauses={pageClauses} />
      </div> */}
    </div >
  );
}
